// El buscador de un chat (13-10, Pau: «un filtro de búsqueda por palabras clave, para cada agente»). Busca en TODA la
// conversación en el servidor (no solo en lo cargado), sin mayúsculas ni tildes y con todas las palabras. Al tocar un
// resultado, si el mensaje está en pantalla lleva a él; si no, lo abre entero ahí mismo.
import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { formatClockTime, formatDiaMes } from '../../../lib/time';
import { oficinaApi } from '../api';
import { normalizarBusqueda } from '../compartido/mensajes';
import type { ContratadoDTO, ResultadoBusquedaDTO } from '../compartido/vocabulario';
import { quien } from '../marco/estado';
import { mensajeError } from '../errores';

const ESPERA_MS = 300;
const ALREDEDOR = 110;

export function Buscador({ contratado: c, alCerrar }: { contratado: ContratadoDTO; alCerrar: () => void }) {
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState<ResultadoBusquedaDTO[] | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);
  useEffect(() => {
    campo.current?.focus();
  }, []);

  useEffect(() => {
    const limpio = q.trim();
    // Lo de la búsqueda anterior no se queda en pantalla mientras llega la nueva.
    setResultados(null);
    if (limpio.length < 2) {
      setCargando(false);
      return;
    }
    let vivo = true;
    const reloj = setTimeout(() => {
      setCargando(true);
      setError(null);
      oficinaApi
        .buscar(c.agentId, limpio)
        .then((r) => vivo && setResultados(r.resultados))
        .catch((e: unknown) => vivo && setError(mensajeError(e)))
        .finally(() => vivo && setCargando(false));
    }, ESPERA_MS);
    return () => {
      vivo = false;
      clearTimeout(reloj);
    };
  }, [q, c.agentId]);

  return (
    // Escape cierra la búsqueda (no el chat) esté donde esté el foco dentro de ella.
    <div
      data-testid="buscador"
      className="absolute inset-0 z-20 flex flex-col bg-oficina-fondo/95 backdrop-blur-sm"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          alCerrar();
        }
      }}
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-oficina-borde px-4 py-2 sm:px-8">
        <Search className="h-4 w-4 shrink-0 text-oficina-texto3" aria-hidden />
        <input
          ref={campo}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('oficinaV2.buscar.placeholder')}
          aria-label={t('oficinaV2.buscar.titulo')}
          enterKeyHint="search"
          className="min-h-11 min-w-0 flex-1 bg-transparent text-o-cuerpo text-oficina-texto outline-none placeholder:text-oficina-texto3"
        />
        <button type="button" onClick={alCerrar} aria-label={t('oficinaV2.buscar.cerrar')} className="flex h-11 w-11 items-center justify-center rounded-full text-oficina-texto2 hover:bg-oficina-hover hover:text-oficina-texto">
          <X className="h-5 w-5" aria-hidden />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-8" aria-live="polite">
        {error ? <p className="text-o-texto text-oficina-error">{error}</p> : null}
        {resultados === null ? (
          <p className="text-o-texto text-oficina-texto3">{cargando ? t('oficinaV2.buscar.buscando') : t('oficinaV2.buscar.pista')}</p>
        ) : resultados.length === 0 ? (
          <p className="text-o-texto text-oficina-texto3">{t('oficinaV2.buscar.nada', { q: q.trim() })}</p>
        ) : (
          <>
            <p className="mb-2 text-o-sub text-oficina-texto3">{t(resultados.length === 1 ? 'oficinaV2.buscar.uno' : 'oficinaV2.buscar.varios', { n: resultados.length })}</p>
            <ol className="space-y-2">
              {resultados.map((r) => (
                <Resultado key={r.messageId} r={r} q={q} nombre={quien(c)} alIr={alCerrar} />
              ))}
            </ol>
          </>
        )}
      </div>
    </div>
  );
}

function Resultado({ r, q, nombre, alIr }: { r: ResultadoBusquedaDTO; q: string; nombre: string; alIr: () => void }) {
  const [entero, setEntero] = useState(false);
  return (
    <li>
      <button
        type="button"
        onClick={() => {
          if (irAlMensaje(r.messageId)) alIr();
          else setEntero(!entero);
        }}
        aria-expanded={entero}
        className="w-full rounded-o-m bg-oficina-elevada px-3.5 py-2.5 text-left hover:bg-oficina-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-oficina-acento"
      >
        <span className="mb-0.5 flex items-center justify-between gap-2 text-o-nota text-oficina-texto3">
          <span className={cn('font-semibold', r.quien === 'pau' ? 'text-oficina-meta-pau' : 'text-oficina-texto2')}>{r.quien === 'pau' ? t('oficinaV2.buscar.tu') : nombre}</span>
          <span>
            {formatDiaMes(r.en)} · {formatClockTime(r.en)}
          </span>
        </span>
        <span className={cn('block whitespace-pre-wrap break-words text-o-texto leading-relaxed text-oficina-texto', !entero && 'line-clamp-3')}>
          {resaltar(entero ? r.texto : fragmento(r.texto, q), q)}
        </span>
      </button>
    </li>
  );
}

/** El trozo del mensaje alrededor de la primera palabra encontrada. */
export function fragmento(texto: string, q: string): string {
  const palabras = normalizarBusqueda(q).split(/\s+/).filter((p) => p.length >= 2);
  const norm = normalizarBusqueda(texto);
  const i = Math.min(...palabras.map((p) => norm.indexOf(p)).filter((x) => x >= 0), Number.POSITIVE_INFINITY);
  if (!Number.isFinite(i) || i < ALREDEDOR) return texto;
  return `…${texto.slice(i - ALREDEDOR / 2)}`;
}

/** Marca las palabras buscadas (sin mayúsculas ni tildes). NFD + quitar marcas conserva el largo en el texto latino normal. */
function resaltar(texto: string, q: string): ReactNode[] {
  const palabras = normalizarBusqueda(q).split(/\s+/).filter((p) => p.length >= 2);
  const norm = normalizarBusqueda(texto);
  if (!palabras.length || norm.length !== texto.length) return [texto];
  const marcas: Array<[number, number]> = [];
  for (const p of palabras) for (let i = norm.indexOf(p); i >= 0; i = norm.indexOf(p, i + p.length)) marcas.push([i, i + p.length]);
  marcas.sort((a, b) => a[0] - b[0]);
  const out: ReactNode[] = [];
  let desde = 0;
  for (const [a, b] of marcas) {
    if (a < desde) continue;
    out.push(texto.slice(desde, a), <mark key={a} className="rounded bg-oficina-acento/30 px-0.5 text-oficina-texto">{texto.slice(a, b)}</mark>);
    desde = b;
  }
  out.push(texto.slice(desde));
  return out;
}

/** Lleva al mensaje si está en pantalla (también si está plegado dentro de otro) y lo resalta 1,5 s. */
function irAlMensaje(messageId: string): boolean {
  const id = CSS.escape(messageId);
  const el = document.querySelector(`[data-message-id="${id}"], [data-message-ids~="${id}"]`);
  if (!(el instanceof HTMLElement)) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('ring-2', 'ring-oficina-enlace');
  setTimeout(() => el.classList.remove('ring-2', 'ring-oficina-enlace'), 1500);
  return true;
}
